import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Message, Modal } from 'semantic-ui-react';
import { API, showError, showSuccess } from '../../helpers';

const emptyForm = {
  tokenIds: '',
  keys: '',
  usernames: '',
  models: [],
};

export const splitBatchValues = (value) => {
  const values = String(value || '')
    .split(/[\n,，;；]+/)
    .map((item) => item.trim())
    .filter(Boolean);
  return [...new Set(values)];
};

export const parsePositiveTokenIds = (value) => {
  const rawIds = splitBatchValues(value);
  const invalid = rawIds.find((item) => {
    if (!/^\d+$/.test(item)) return true;
    const id = Number(item);
    return !Number.isSafeInteger(id) || id <= 0;
  });
  if (invalid) {
    return { values: [], invalid };
  }
  return { values: [...new Set(rawIds.map(Number))], invalid: '' };
};

export const normalizeModelNames = (models) => [
  ...new Set(
    (Array.isArray(models) ? models : [])
      .map((model) => (typeof model === 'string' ? model : model?.id))
      .filter((model) => typeof model === 'string' && model.length > 0)
  ),
];

const BatchResult = ({ result, t }) => (
  <Message success>
    <Message.Header>{t('token.batch_add.result.title')}</Message.Header>
    <Message.List>
      <Message.Item>
        {t('token.batch_add.result.matched', {
          count: result.matched_token_count || 0,
        })}
      </Message.Item>
      <Message.Item>
        {t('token.batch_add.result.updated', {
          count: result.updated_token_count || 0,
          models: result.added_model_count || 0,
        })}
      </Message.Item>
      <Message.Item>
        {t('token.batch_add.result.skipped', {
          satisfied: result.already_satisfied_token_count || 0,
          unrestricted: result.unrestricted_token_count || 0,
        })}
      </Message.Item>
      <Message.Item>
        {t('token.batch_add.result.not_found', {
          ids: result.not_found_token_ids?.length || 0,
          keys: result.not_found_key_count || 0,
          usernames: result.not_found_username_count || 0,
        })}
      </Message.Item>
      {(result.cache_invalidation_failed_count || 0) > 0 && (
        <Message.Item>
          {t('token.batch_add.result.cache_failed', {
            count: result.cache_invalidation_failed_count,
          })}
        </Message.Item>
      )}
    </Message.List>
  </Message>
);

const BatchAddTokenModelsModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(emptyForm);
  const [modelOptions, setModelOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    setForm(emptyForm);
    setResult(null);

    let active = true;
    const loadModels = async () => {
      try {
        const response = await API.get('/api/channel/models');
        const models = normalizeModelNames(response.data?.data);
        if (!active) return;
        setModelOptions(
          models.map((model) => ({
            key: model,
            text: model,
            value: model,
          }))
        );
      } catch (error) {
        showError(
          error.response?.data?.message ||
            t('token.batch_add.messages.load_models_failed')
        );
      }
    };
    loadModels();
    return () => {
      active = false;
    };
  }, [open, t]);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setResult(null);
  };

  const submit = async () => {
    const tokenIds = parsePositiveTokenIds(form.tokenIds);
    if (tokenIds.invalid) {
      showError(
        t('token.batch_add.messages.invalid_token_id', {
          value: tokenIds.invalid,
        })
      );
      return;
    }
    const keys = splitBatchValues(form.keys);
    const usernames = splitBatchValues(form.usernames);
    if (
      tokenIds.values.length === 0 &&
      keys.length === 0 &&
      usernames.length === 0
    ) {
      showError(t('token.batch_add.messages.selector_required'));
      return;
    }
    if (form.models.length === 0) {
      showError(t('token.batch_add.messages.models_required'));
      return;
    }

    setSubmitting(true);
    try {
      const response = await API.post('/api/admin/tokens/models/batch-add', {
        token_ids: tokenIds.values,
        keys,
        usernames,
        models: form.models,
      });
      if (!response.data?.success) {
        showError(
          response.data?.message || t('token.batch_add.messages.failed')
        );
        return;
      }
      setResult(response.data.data || {});
      showSuccess(t('token.batch_add.messages.success'));
    } catch (error) {
      showError(
        error.response?.data?.message || t('token.batch_add.messages.failed')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={submitting ? undefined : onClose} size='small'>
      <Modal.Header>{t('token.batch_add.title')}</Modal.Header>
      <Modal.Content scrolling>
        <Message info>{t('token.batch_add.description')}</Message>
        <Form>
          <Form.TextArea
            label={t('token.batch_add.token_ids')}
            placeholder={t('token.batch_add.token_ids_placeholder')}
            value={form.tokenIds}
            onChange={(event) => updateForm('tokenIds', event.target.value)}
            disabled={submitting}
          />
          <Form.TextArea
            label={t('token.batch_add.keys')}
            placeholder={t('token.batch_add.keys_placeholder')}
            value={form.keys}
            onChange={(event) => updateForm('keys', event.target.value)}
            disabled={submitting}
          />
          <Form.TextArea
            label={t('token.batch_add.usernames')}
            placeholder={t('token.batch_add.usernames_placeholder')}
            value={form.usernames}
            onChange={(event) => updateForm('usernames', event.target.value)}
            disabled={submitting}
          />
          <Form.Dropdown
            required
            fluid
            multiple
            search
            selection
            allowAdditions
            label={t('token.batch_add.models')}
            placeholder={t('token.batch_add.models_placeholder')}
            options={modelOptions}
            value={form.models}
            disabled={submitting}
            onChange={(event, { value }) =>
              updateForm('models', [...new Set(value)])
            }
            onAddItem={(event, { value }) => {
              setModelOptions((current) => [
                ...current,
                { key: value, text: value, value },
              ]);
            }}
          />
        </Form>
        {result && <BatchResult result={result} t={t} />}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={submitting}>
          {t('token.batch_add.cancel')}
        </Button>
        <Button primary loading={submitting} onClick={submit}>
          {t('token.batch_add.submit')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default BatchAddTokenModelsModal;
